import type { AnswerValue, FieldId } from '@inside-us-aos/domain';

import type { AnswerStore } from '../state/answerStore';

/**
 * Audit-event emitter (with PII redaction).
 *
 * USCIS compliance treats every answer mutation as an auditable
 * action. The audit emitter subscribes to an `AnswerStore`'s change
 * stream and forwards each mutation as a stable, structured
 * `AuditEvent`. Phase 5 will wire this stream to a persistence
 * adapter; Phase 2 may render an audit-trail debug panel from it.
 *
 * ### Event schema (stable contract)
 *
 * ```ts
 * interface AuditEvent {
 *   type: 'answer_changed';
 *   timestamp: number;          // wall-clock millis
 *   fieldId: FieldId;
 *   instanceKey?: string;       // groupInstanceId; absent for singleton fields
 *   oldValue?: AnswerValue | RedactedValue;  // RedactedValue when fieldId is in `redactedFields`
 *   newValue?: AnswerValue | RedactedValue;  // RedactedValue when fieldId is in `redactedFields`
 *   reason?: string;            // free-form annotation supplied by the caller
 * }
 * ```
 *
 * The schema is **stable** and intentionally decoupled from the
 * store's internal representation. We translate the store's
 * `groupInstanceId: string | undefined` into the audit-trail's
 * `instanceKey?: string` (omitted when absent) so consumers can
 * project audit events into the domain `AuditEvent` shape without
 * peeking at engine internals.
 *
 * ### Redaction contract (P1 fix from Phase 1 review)
 *
 * The Phase 1 review (and ADR 0013, now updated) flagged that the
 * raw `oldValue` / `newValue` payloads leak PII (SSN, A-numbers,
 * dates of birth, addresses, etc.) into any audit-event subscriber.
 * Phase 5's persistence layer will write events durably, which
 * makes the leak real.
 *
 * `createAuditEmitter` accepts an optional `redactedFields:
 * ReadonlySet<FieldId>` parameter. When a change event's `fieldId`
 * is in this set, the emitter replaces the `oldValue` / `newValue`
 * payloads with a sentinel `{ type: 'redacted', reason: 'pii' }`
 * record. The `fieldId`, `instanceKey`, and `timestamp` are still
 * emitted — those metadata fields are needed to reconcile audit
 * trails and don't themselves carry the protected payload.
 *
 * The default redaction set is empty. The form-engine façade
 * (`createFormEngine`) computes a sensible PII set from field-id
 * patterns and passes it in by default; advanced consumers can
 * still override.
 *
 * ### Behavior
 *
 * - Audit emission is fire-and-forget. Listener errors are caught
 *   and re-thrown asynchronously (so the next listener still
 *   fires and the store's `setAnswer` call returns normally).
 * - Multiple listeners fan out; each receives the same event.
 * - `subscribe(listener)` returns an `unsubscribe` function.
 * - The emitter does **not** await listeners. Persistence adapters
 *   that need durability must buffer events themselves.
 */

/**
 * Sentinel value substituted for raw answer payloads when a field
 * is in the emitter's `redactedFields` set. The `reason` is a
 * stable string ("pii") so downstream consumers can branch.
 * Carries no plaintext.
 */
export interface RedactedValue {
  type: 'redacted';
  reason: 'pii';
}

const REDACTED_VALUE: RedactedValue = { type: 'redacted', reason: 'pii' };

export type AuditPayload = AnswerValue | RedactedValue;

export interface AuditEvent {
  type: 'answer_changed';
  timestamp: number;
  fieldId: FieldId;
  instanceKey?: string;
  oldValue?: AuditPayload;
  newValue?: AuditPayload;
  reason?: string;
}

export type AuditEventListener = (event: AuditEvent) => void;

export interface AuditEmitter {
  /** Subscribes a listener. Returns the unsubscribe function. */
  subscribe: (listener: AuditEventListener) => () => void;
  /** Idempotent unsubscribe. */
  unsubscribe: (listener: AuditEventListener) => void;
  /**
   * Force-flushes every pending coalesced bucket immediately. Callers
   * use this to mark a "stable" point in the user's session (section
   * navigation, explicit save, tab hide) so the audit trail records
   * the value at that boundary rather than waiting for the
   * coalesceMs timer to fire. Idempotent — a flush with no pending
   * events is a no-op.
   */
  flush: () => void;
  /**
   * Suspend audit emission until the returned `resume` function is
   * called. While suspended, the emitter still receives store
   * change events, but it does NOT buffer, coalesce, or broadcast
   * them — they are dropped on the floor. Used by `formEngine.hydrate`
   * to silence the audit trail during snapshot replay without
   * affecting other store subscribers (React re-render, autosave).
   *
   * Calls nest: each `suspend()` adds one level of suspension; the
   * emitter resumes broadcasting only when every paired `resume`
   * has run. Returned thunk is idempotent (calling twice is safe).
   */
  suspend: () => () => void;
  /** Detaches the emitter from the underlying store. Subsequent answer
   *  changes will no longer produce audit events. */
  dispose: () => void;
}

export interface AuditEmitterOptions {
  /**
   * Field ids whose `oldValue` / `newValue` payloads must be
   * redacted in emitted events. Pass a precomputed set; the
   * emitter does not derive it from the catalog itself. The
   * `createFormEngine` façade computes a default PII set from
   * field-id patterns and forwards it here.
   */
  redactedFields?: ReadonlySet<FieldId>;

  /**
   * If set, consecutive events on the same `(fieldId, instanceKey)`
   * tuple within `coalesceMs` milliseconds are coalesced into a
   * single emission. Useful for collapsing per-keystroke noise into
   * one event per "stable state" of a field. The coalesced event
   * has:
   *   - `oldValue`: the FIRST event's `oldValue` (the original
   *     state before the burst began).
   *   - `newValue`: the LAST event's `newValue` (the final state
   *     after the burst ended). When the last event is a `clear`
   *     (no `newValue`), the coalesced event also omits `newValue`,
   *     preserving the clear semantics (i.e. set→set→clear becomes
   *     a single event with `oldValue = first.oldValue` and no
   *     `newValue`).
   *   - `timestamp`: the LAST event's `timestamp` (most recent).
   *
   * Recommended consumer setting: 800–1200 ms — long enough that
   * natural typing collapses, short enough that pause-and-resume
   * registers as separate events. When `coalesceMs` is `undefined`
   * (default) emission is per-event (back-compat with bare
   * emitter).
   *
   * Coalescing happens AFTER PII redaction; redacted values still
   * coalesce correctly because the tuple key is the field
   * identity, not the value.
   *
   * Trade-off: the FIRST event of a burst is buffered (not emitted
   * immediately). Subscribers only see the coalesced event after
   * the timer fires. This is intentional — the panel should show
   * stable states, not transient ones. `dispose()` (and
   * `unsubscribe()` of the last listener) flushes pending timers
   * synchronously so no event is lost on teardown.
   *
   * Override semantics: 0 disables coalescing (treated as
   * `undefined`). Negative values are rejected.
   */
  coalesceMs?: number;
}

/**
 * Creates an audit emitter bound to an `AnswerStore`. The emitter
 * subscribes once to the store and broadcasts a translated
 * `AuditEvent` to its own subscribers on every answer mutation.
 *
 * When `options.redactedFields` is provided, mutations on those
 * fields emit a sentinel `RedactedValue` in place of the raw
 * `oldValue` / `newValue`. Other event metadata (fieldId,
 * instanceKey, timestamp) is unchanged.
 */
/**
 * Buffered state for one `(fieldId, instanceKey)` tuple while a
 * coalescing window is open. We retain the FIRST event's `oldValue`
 * (the pre-burst state) and continually replace the rest with the
 * LAST observed event so that emission carries
 * (firstOldValue → lastNewValue) at lastTimestamp.
 */
interface CoalesceBucket {
  /** The `oldValue` from the first buffered event (may be undefined when the field had no prior value). */
  firstOldValue: AuditPayload | undefined;
  /** Whether `oldValue` was present on the first event. Tracked separately so we can omit `oldValue` from the coalesced event when the first event omitted it (initial set on a previously-empty field). */
  firstHadOldValue: boolean;
  /** The most recently observed event in the burst. Its `newValue` (and presence-of-newValue) wins. */
  last: AuditEvent;
  /** Active timer handle (Node `Timeout` or browser `number`). Reset on every new event. */
  timer: ReturnType<typeof setTimeout>;
}

const tupleKeyFor = (
  fieldId: FieldId,
  instanceKey: string | undefined,
): string => `${fieldId}::${instanceKey ?? '__SINGLETON__'}`;

export const createAuditEmitter = (
  store: AnswerStore,
  options: AuditEmitterOptions = {},
): AuditEmitter => {
  const listeners = new Set<AuditEventListener>();
  const redactedFields = options.redactedFields;
  const rawCoalesceMs = options.coalesceMs;
  if (rawCoalesceMs !== undefined && rawCoalesceMs < 0) {
    throw new Error(
      `createAuditEmitter: coalesceMs must be >= 0 (got ${rawCoalesceMs}).`,
    );
  }
  // 0 is treated as "disabled" — same shape as `undefined`.
  const coalesceMs =
    rawCoalesceMs === undefined || rawCoalesceMs === 0
      ? undefined
      : rawCoalesceMs;
  const buckets = new Map<string, CoalesceBucket>();

  const broadcast = (event: AuditEvent): void => {
    // Snapshot to insulate against mid-iteration unsubscribes.
    const snapshot = [...listeners];
    for (const listener of snapshot) {
      try {
        listener(event);
      } catch (err) {
        // A misbehaving audit listener must not break the store or
        // its peers. Re-throw on a microtask so test runners and
        // top-level error handlers still see the failure, but the
        // current `setAnswer` call returns normally.
        const error = err;
        queueMicrotask(() => {
          throw error;
        });
      }
    }
  };

  /** Synthesize the coalesced event from a bucket and broadcast it. */
  const flushBucket = (key: string): void => {
    const bucket = buckets.get(key);
    if (!bucket) return;
    clearTimeout(bucket.timer);
    buckets.delete(key);
    // Build the coalesced event from `last`, but with `oldValue`
    // pulled from `firstOldValue` (the pre-burst state).
    const { last, firstOldValue, firstHadOldValue } = bucket;
    const coalesced: AuditEvent = {
      type: 'answer_changed',
      timestamp: last.timestamp,
      fieldId: last.fieldId,
      ...(last.instanceKey !== undefined
        ? { instanceKey: last.instanceKey }
        : {}),
      ...(firstHadOldValue && firstOldValue !== undefined
        ? { oldValue: firstOldValue }
        : {}),
      ...(last.newValue !== undefined ? { newValue: last.newValue } : {}),
    };
    broadcast(coalesced);
  };

  const flushAll = (): void => {
    // Snapshot keys because flushBucket mutates the map.
    const keys = [...buckets.keys()];
    for (const key of keys) flushBucket(key);
  };

  // Suspension counter — supports nested suspend()/resume() pairs.
  // The store subscriber short-circuits while > 0.
  let suspendDepth = 0;

  const unsubscribeFromStore = store.subscribe(change => {
    if (suspendDepth > 0) return;
    const isRedacted = !!redactedFields?.has(change.fieldId);
    const projectPayload = (
      raw: AnswerValue | undefined,
    ): AuditPayload | undefined => {
      if (raw === undefined) return undefined;
      return isRedacted ? REDACTED_VALUE : raw;
    };
    const oldPayload = projectPayload(change.oldValue);
    const newPayload = projectPayload(change.newValue);
    const event: AuditEvent = {
      type: 'answer_changed',
      timestamp: change.timestamp,
      fieldId: change.fieldId,
      ...(change.groupInstanceId !== undefined
        ? { instanceKey: change.groupInstanceId }
        : {}),
      ...(oldPayload !== undefined ? { oldValue: oldPayload } : {}),
      ...(newPayload !== undefined ? { newValue: newPayload } : {}),
    };

    if (coalesceMs === undefined) {
      broadcast(event);
      return;
    }

    const key = tupleKeyFor(change.fieldId, change.groupInstanceId);
    const existing = buckets.get(key);
    if (existing) {
      // Extend the burst: replace `last`, reset the timer; keep
      // `firstOldValue` so the coalesced event still walks back to
      // the pre-burst state.
      clearTimeout(existing.timer);
      existing.last = event;
      existing.timer = setTimeout(() => flushBucket(key), coalesceMs);
    } else {
      // Open a new burst. The first event is buffered, not emitted
      // immediately — see the JSDoc trade-off note on
      // `AuditEmitterOptions.coalesceMs`.
      const bucket: CoalesceBucket = {
        firstOldValue: oldPayload,
        firstHadOldValue: oldPayload !== undefined,
        last: event,
        timer: setTimeout(() => flushBucket(key), coalesceMs),
      };
      buckets.set(key, bucket);
    }
  });

  return {
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    unsubscribe: listener => {
      listeners.delete(listener);
    },
    flush: flushAll,
    suspend: () => {
      suspendDepth += 1;
      let resumed = false;
      return () => {
        if (resumed) return;
        resumed = true;
        suspendDepth = Math.max(0, suspendDepth - 1);
      };
    },
    dispose: () => {
      // Flush pending coalesced events synchronously so callers
      // don't lose the tail of a burst when teardown races a timer.
      flushAll();
      unsubscribeFromStore();
      listeners.clear();
    },
  };
};
